import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ConsentProvider, useConsent } from './contexts/ConsentContext'
import { FloatingWidgetsContainer, FloatingWidget } from './contexts/FloatingWidgetsContext'
import { ServerConfigProvider } from './contexts/ServerConfigContext'
import { HomePage } from './components/home/HomePage'
import Dashboard from './components/dashboard/Dashboard'
import { Library } from './components/library/Library'
import { BookDetailPage } from './components/library/BookDetailPage'
import { Settings } from './components/settings/Settings'
import { Profile } from './components/profile/Profile'
import { ReferralsPage } from './components/referrals/ReferralsPage'
import { TransactionsPage } from './components/transactions/TransactionsPage'
import { Upload } from './components/upload/Upload'
import LoginPage from './components/auth/LoginPage'
import { AuthCallback } from './components/auth/AuthCallback'
import { PaymentCompletePage } from './components/payment/PaymentCompletePage'
import { PaymentCancelledPage } from './components/payment/PaymentCancelledPage'
import { AdminRoute } from './components/admin/AdminRoute'
import { AdminDashboard } from './components/admin/AdminDashboard'
import { AdminManagementPage } from './components/admin/AdminManagementPage'
import { AdminMembershipManagement } from './components/admin/AdminMembershipManagement'
import { AdminBookForm } from './components/admin/AdminBookForm'
import { AdminBookDetail } from './components/admin/AdminBookDetail'
import { AdminMultiUpload } from './components/admin/AdminMultiUpload'
import { AdminSettings } from './components/admin/AdminSettings'
import { UserBookDetail } from './components/user/UserBookDetail'
import { UserBookForm } from './components/user/UserBookForm'
import { ReaderPage } from './components/reader/ReaderPage'
import { PreviewPage } from './components/reader/PreviewPage'
import { TranslationProgressWidget } from './components/translations/TranslationProgressWidget'
import { RewardClaimWidget } from './components/rewards/RewardClaimWidget'
import { OnboardingFloatingWidget } from './components/onboarding/OnboardingFloatingWidget'
import { CookieBanner, ConsentModal } from './components/consent'
import { Footer } from './components/shared/Footer'
import './App.css'

function App() {
  // Capture referral code from URL on any page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const referralCode = urlParams.get('ref')
    if (referralCode && !sessionStorage.getItem('referral_code')) {
      sessionStorage.setItem('referral_code', referralCode)
      console.log('Captured referral code:', referralCode)
    }
  }, [])

  return (
    <ServerConfigProvider>
      <AuthProvider>
        <ConsentProvider>
          <AppContent />
        </ConsentProvider>
      </AuthProvider>
    </ServerConfigProvider>
  )
}

function AppContent() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { openModal } = useConsent()
  
  // Handle ?manage_cookies=1 parameter from static pages
  useEffect(() => {
    if (searchParams.get('manage_cookies') === '1') {
      openModal()
      // Remove the parameter from URL
      searchParams.delete('manage_cookies')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, openModal])

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1">
        <AppRoutes />
      </div>
      <Footer />
      
      {/* Cookie consent UI */}
      <CookieBanner />
      <ConsentModal />
      
      {/* Floating widgets - stacked vertically from bottom */}
      <FloatingWidgetsContainer>
        {/* Order: bottom to top (flex-col-reverse), last = top */}
        <FloatingWidget><TranslationProgressWidget /></FloatingWidget>
        <FloatingWidget><OnboardingFloatingWidget /></FloatingWidget>
        <FloatingWidget><RewardClaimWidget mode="mobile-floating" /></FloatingWidget>
      </FloatingWidgetsContainer>
    </div>
  )
}

function AppRoutes() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <Routes>
      <Route 
        path="/" 
        element={<HomePage />}
      />
      <Route 
        path="/library" 
        element={<Library />}
      />
      <Route 
        path="/library/books/:bookId" 
        element={<BookDetailPage />}
      />
      <Route 
        path="/my-books" 
        element={user ? <Dashboard /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/upload" 
        element={user ? <Upload /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/settings" 
        element={user ? <Settings /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/profile" 
        element={user ? <Profile /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/referrals" 
        element={user ? <ReferralsPage /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/transactions" 
        element={user ? <TransactionsPage /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <LoginPage />} 
      />
      <Route 
        path="/auth/callback" 
        element={<AuthCallback />} 
      />
      {/* Payment Routes - These pages auto-close after Stripe checkout */}
      <Route 
        path="/payment/complete" 
        element={<PaymentCompletePage />} 
      />
      <Route 
        path="/payment/cancelled" 
        element={<PaymentCancelledPage />} 
      />
      {/* User Book Routes */}
      <Route 
        path="/user/books/new" 
        element={user ? <UserBookForm /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/user/books/:bookId" 
        element={user ? <UserBookDetail /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/user/books/:bookId/translations/:translationId" 
        element={user ? <UserBookDetail /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/user/books/:bookId/edit" 
        element={user ? <UserBookForm /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      {/* Online Reader Route */}
      <Route 
        path="/read/:bookId/:translationId" 
        element={user ? <ReaderPage /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      <Route 
        path="/preview/:bookId/:translationId" 
        element={user ? <PreviewPage /> : <Navigate to="/login" replace state={{ from: location }} />} 
      />
      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={<AdminRoute><AdminDashboard /></AdminRoute>} 
      />
      <Route 
        path="/admin/management" 
        element={<AdminRoute><AdminManagementPage /></AdminRoute>} 
      />
      <Route 
        path="/admin/memberships" 
        element={<AdminRoute><AdminMembershipManagement /></AdminRoute>} 
      />
      <Route 
        path="/admin/books/new" 
        element={<AdminRoute><AdminBookForm /></AdminRoute>} 
      />
      <Route 
        path="/admin/books/bulk-upload" 
        element={<AdminRoute><AdminMultiUpload /></AdminRoute>} 
      />
      <Route 
        path="/admin/books/:bookId" 
        element={<AdminRoute><AdminBookDetail /></AdminRoute>} 
      />
      <Route 
        path="/admin/books/:bookId/edit" 
        element={<AdminRoute><AdminBookForm /></AdminRoute>} 
      />
      <Route 
        path="/admin/settings" 
        element={<AdminRoute><AdminSettings /></AdminRoute>} 
      />
    </Routes>
  )
}

export default App
